import type { QueryClient } from '@tanstack/react-query'

import { PaymentMethod } from '@ankor-io/common/types/transaction/transaction'

type InvalidateBoatCashFloatQueriesParams = {
  queryClient: QueryClient
  organizationId?: string | null
  vesselId?: string | null
  paymentMethod?: PaymentMethod | string
  cashFloatId?: string | null
}

type TransactionBoatCashFields = {
  paymentMethod?: PaymentMethod | string
  cashFloatId?: string | null
  cashFloat?: { id: string } | null
}

/**
 * Refreshes cash-float queries after a boat-cash manual expense when a float is linked.
 * Uses vessel-scoped keys so APA and OPS module caches both refresh.
 */
export function invalidateBoatCashFloatQueries({
  queryClient,
  organizationId,
  vesselId,
  paymentMethod,
  cashFloatId,
}: InvalidateBoatCashFloatQueriesParams): void {
  const floatId = cashFloatId?.trim()
  if (paymentMethod !== PaymentMethod.BOAT_CASH || !floatId) {
    return
  }

  if (!organizationId || !vesselId) {
    return
  }

  queryClient.invalidateQueries({
    queryKey: ['cash-float', organizationId, vesselId],
  })
  queryClient.invalidateQueries({
    queryKey: ['cash-float-activity', organizationId, vesselId],
  })
  queryClient.invalidateQueries({
    queryKey: ['cash-floats', organizationId, vesselId],
  })
  queryClient.invalidateQueries({
    queryKey: ['cash-summary', organizationId, vesselId],
  })
}

export function invalidateBoatCashFloatQueriesForTransaction({
  queryClient,
  organizationId,
  vesselId,
  transaction,
}: {
  queryClient: QueryClient
  organizationId?: string | null
  vesselId?: string | null
  transaction?: TransactionBoatCashFields | null
}): void {
  if (!transaction) {
    return
  }

  invalidateBoatCashFloatQueries({
    queryClient,
    organizationId,
    vesselId,
    paymentMethod: transaction.paymentMethod,
    cashFloatId: transaction.cashFloatId ?? transaction.cashFloat?.id,
  })
}
